import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  CenterFocusStrong,
  FactCheck,
  Lock,
  Redo,
  Undo,
  ViewAgenda,
} from '@mui/icons-material';
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  NodeChange,
  OnConnectStartParams,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  XYPosition,
} from 'reactflow';
import 'reactflow/dist/style.css';

import {
  BaukastenContext,
  BaukastenSelection,
  EDGE_COLOR,
  ICardDropIndicator,
  IBaukastenContext,
  kindColor,
  kindIcon,
  NODE_TYPES,
  targetKey,
} from './BaukastenNodes';
import { BaukastenInspector } from './BaukastenInspector';
import {
  buildGraph,
  canConnect,
  checkNodeId,
  conditionsOnQuestions,
  connect,
  dropConditionsOn,
  findNodePath,
  IExternalQuestion,
  insertMarkerY,
  insertNode,
  insertPointAt,
  insertSectionAt,
  ITargetRef,
  LANE_BASE,
  LANE_STEP,
  locateQuestions,
  moveNode,
  moveSectionTo,
  nodeAtPath,
  questionOverview,
  removeNode,
  renameQuestionReferences,
  replaceCheckEverywhere,
  replaceNode,
  rowKey,
  rowTargetHandle,
  SECTION_TARGET,
  SECTION_WIDTH,
  sectionAt,
  CHECK_WIDTH,
  sectionInsertIndexAt,
  selfGatingIssues,
  setConditionAt,
  usedQuestionIds,
} from './baukastenModel';
import {
  duplicateNode,
  ICondition,
  IEditorNode,
  IEditorSection,
  NODE_KIND_HINT,
  NODE_KIND_LABEL,
  NodeKind,
  NodePath,
  newNode,
  replaceAt,
  walkNodes,
} from './editorModel';
import { FieldLabelResolver } from './fieldPathLabel';
import { IArnoValidationIssue } from '../../hooks/reactQuerys/ArnoAdminQueries';
import { useAlert } from '../../context/AlertContext';
import { useConfirmation } from '../../context/ConfirmationContext';

/**
 * Der visuelle Baukasten für Entscheidungsbäume (#1245).
 *
 * Eine Ansicht im Editor, keine eigene Seite: Speichern, Prüfen, Testen und Veröffentlichen
 * bleiben oben im Editor, und der Baukasten arbeitet auf denselben Abschnitten wie die Liste.
 * Man kann jederzeit wechseln — es gibt nichts zu übertragen.
 *
 * Was auf der Fläche passiert, steht in `baukastenModel` (reine, geprüfte Funktionen); hier
 * geht es um Bedienung: Ziehen, Ablegen, Verbinden, Auswählen, Rückgängig.
 */

interface Props {
  sections: IEditorSection[];
  onChange: (_sections: IEditorSection[]) => void;
  editable: boolean;
  questions: { questionId: string; prompt: string; answerType: string }[];
  externalQuestions: IExternalQuestion[];
  subBlocks: { blockKey: string; name: string }[];
  idLegalTransaction: number | null;
  issues: IArnoValidationIssue[];
  /** Der Abschnitt, der auch in der Liste offen ist — beide Ansichten bleiben beieinander. */
  focusSectionIndex: number;
  onFocusSection: (_index: number) => void;
  /** Wechselt nach Laden und Speichern; dann beginnt die Rückgängig-Liste neu. */
  resetKey: string | null;
  fieldLabel: FieldLabelResolver;
  onOpenInList: (_index: number) => void;
}

const DND_TYPE = 'application/x-dino-baukasten';

type DragPayload =
  | { kind: NodeKind }
  | { section: true }
  | { check: true }
  | { move: { sectionIndex: number; path: NodePath } };

type DropState =
  | { type: 'card'; indicator: ICardDropIndicator }
  | { type: 'gap'; index: number; label: string }
  | null;

interface IPaletteItem {
  id: string;
  label: string;
  hint: string;
  icon: React.ReactElement;
  color: string;
  payload: DragPayload;
}

const nodeItem = (kind: NodeKind): IPaletteItem => ({
  id: kind,
  label: NODE_KIND_LABEL[kind],
  hint: NODE_KIND_HINT[kind] ?? '',
  icon: kindIcon(kind),
  color: kindColor(kind),
  payload: { kind },
});

const PALETTE: { title: string; items: IPaletteItem[] }[] = [
  {
    title: 'Aufbau',
    items: [
      {
        id: 'section',
        label: 'Abschnitt',
        hint: 'Ein Teil der Urkunde, z. B. »§ 3 Kaufpreis«.',
        icon: <ViewAgenda fontSize='inherit' />,
        color: '#37474f',
        payload: { section: true },
      },
      nodeItem('text'),
      nodeItem('data_field'),
      nodeItem('table'),
    ],
  },
  {
    title: 'Entscheiden',
    items: [
      nodeItem('question'),
      nodeItem('conditional_text'),
      {
        id: 'check',
        label: 'Prüfung Vorgangsdaten',
        hint: 'Entscheidet nach einem Wert aus dem Vorgang, z. B. »mehrere Käufer«.',
        icon: <FactCheck fontSize='inherit' />,
        color: EDGE_COLOR.field,
        payload: { check: true },
      },
      nodeItem('choice'),
      nodeItem('input'),
    ],
  },
  {
    title: 'Weitere',
    items: [
      nodeItem('block_reference'),
      nodeItem('conditional_block_reference'),
      nodeItem('info'),
      nodeItem('stop'),
    ],
  },
];

const sameTarget = (
  a: { sectionIndex: number; path: NodePath | null },
  b: ITargetRef
): boolean =>
  a.sectionIndex === b.sectionIndex &&
  (a.path === null) === (b.path === null) &&
  rowKey(a.path ?? []) === rowKey(b.path ?? []);

/**
 * Größenmeldungen im nächsten Bild ausliefern, solange der Baukasten offen ist.
 *
 * Ändert sich beim Ziehen eine Kartengröße (ein Element wechselt die Karte), meldet der Browser
 * das über `ResizeObserver`, React Flow zeichnet darauf sofort neu — und ändert sich dabei wieder
 * eine Größe, bricht der Browser die Runde mit »ResizeObserver loop completed with undelivered
 * notifications« ab. Harmlos, aber das Dev-Overlay von react-scripts legt sich damit über die
 * ganze Seite, und abfangen lässt sich die Meldung nicht: das Overlay hängt als erstes am
 * Fehlerereignis des Fensters, und dort laufen die Empfänger in der Reihenfolge ihrer Anmeldung.
 *
 * Kommt die Meldung ein Bild später, entsteht die Schleife gar nicht erst. Gilt nur, solange der
 * Baukasten offen ist; die Rückgabe stellt den ursprünglichen `ResizeObserver` wieder her.
 */
const deferResizeObserver = (): (() => void) => {
  const Original = window.ResizeObserver;
  if (!Original || 'deferred' in Original) {
    return () => undefined;
  }
  class Deferred extends Original {
    static deferred = true;

    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  }
  window.ResizeObserver = Deferred;
  return () => {
    if (window.ResizeObserver === Deferred) {
      window.ResizeObserver = Original;
    }
  };
};

const isTyping = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  if (!element) {
    return false;
  }
  return (
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ||
    element.isContentEditable
  );
};

const CanvasInner: React.FC<Props> = ({
  sections,
  onChange,
  editable,
  questions,
  externalQuestions,
  subBlocks,
  idLegalTransaction,
  issues,
  focusSectionIndex,
  onFocusSection,
  resetKey,
  fieldLabel,
  onOpenInList,
}) => {
  const flow = useReactFlow();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const canvasRef = useRef<HTMLDivElement>(null);

  const [selection, setSelection] = useState<BaukastenSelection | null>(null);
  const [focusQuestionState, setFocusQuestion] = useState<string | null>(null);
  const [pendingChecks, setPendingChecks] = useState<
    { id: string; condition: ICondition; nearY?: number }[]
  >([]);
  const [dropState, setDropState] = useState<DropState>(null);
  const [dragOverride, setDragOverride] = useState<{
    id: string;
    position: XYPosition;
  } | null>(null);

  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  // Vor dem ersten Zeichnen der Fläche (React Flow legt seine Observer beim Rendern an), und
  // beim Verlassen wieder zurück — siehe `deferResizeObserver`.
  const [restoreResizeObserver] = useState(deferResizeObserver);
  useEffect(() => restoreResizeObserver, [restoreResizeObserver]);

  const dragPayload = useRef<DragPayload | null>(null);
  const dropKey = useRef('');
  const connectStart = useRef<OnConnectStartParams | null>(null);
  const connected = useRef(false);

  // -------------------------------------------------------------------------
  // Rückgängig / Wiederholen
  // -------------------------------------------------------------------------
  const history = useRef<{
    past: IEditorSection[][];
    future: IEditorSection[][];
    key: string | null;
    at: number;
  }>({ past: [], future: [], key: null, at: 0 });
  const [, setHistoryVersion] = useState(0);

  useEffect(() => {
    history.current = { past: [], future: [], key: null, at: 0 };
    setHistoryVersion(version => version + 1);
  }, [resetKey]);

  /**
   * Eine Änderung übernehmen. Tippen im Eigenschaftenbereich erzeugt viele kleine Änderungen
   * an derselben Stelle; mit `coalesce` werden sie zu einem Schritt, sonst müsste man für ein
   * Wort zehnmal rückgängig machen.
   */
  const commit = useCallback(
    (next: IEditorSection[], coalesce?: string) => {
      const current = sectionsRef.current;
      if (next === current) {
        return;
      }
      const entry = history.current;
      const now = Date.now();
      if (!(coalesce && entry.key === coalesce && now - entry.at < 1500)) {
        entry.past.push(current);
        if (entry.past.length > 100) {
          entry.past.shift();
        }
      }
      entry.key = coalesce ?? null;
      entry.at = now;
      entry.future = [];
      setHistoryVersion(version => version + 1);
      onChange(next);
    },
    [onChange]
  );

  const undo = useCallback(() => {
    const entry = history.current;
    const previous = entry.past.pop();
    if (!previous) {
      return;
    }
    entry.future.push(sectionsRef.current);
    entry.key = null;
    setHistoryVersion(version => version + 1);
    onChange(previous);
  }, [onChange]);

  const redo = useCallback(() => {
    const entry = history.current;
    const next = entry.future.pop();
    if (!next) {
      return;
    }
    entry.past.push(sectionsRef.current);
    entry.key = null;
    setHistoryVersion(version => version + 1);
    onChange(next);
  }, [onChange]);

  // -------------------------------------------------------------------------
  // Graph und abgeleitete Angaben
  // -------------------------------------------------------------------------
  const graph = useMemo(
    () => buildGraph(sections, externalQuestions, pendingChecks),
    [sections, externalQuestions, pendingChecks]
  );
  const graphRef = useRef(graph);
  graphRef.current = graph;

  /**
   * Anfangsansicht: der Anfang der Urkunde, lesbar — nicht der ganze Baum in Briefmarkengröße.
   * Berechnet aus dem Layout statt eingepasst: React Flow kennt die Kartengrößen erst nach dem
   * Messen, das Layout kennt sie sofort.
   */
  const showDocumentStart = useCallback(
    (instance: {
      setViewport: (_viewport: { x: number; y: number; zoom: number }) => void;
    }) => {
      const bounds = canvasRef.current?.getBoundingClientRect();
      if (!bounds?.width) {
        return;
      }
      const current = graphRef.current;
      const hasSide = current.checks.length > 0 || current.externals.length > 0;
      const contentWidth = hasSide
        ? current.sideColumnX + CHECK_WIDTH
        : SECTION_WIDTH + LANE_BASE + 160;
      const zoom = Math.max(
        0.45,
        Math.min(0.95, (bounds.width - 80) / contentWidth)
      );
      instance.setViewport({
        x: Math.max(24, (bounds.width - contentWidth * zoom) / 2),
        // Unter der Werkzeugleiste oben links, nicht dahinter.
        y: 72,
        zoom,
      });
    },
    []
  );

  const prompts = useMemo(() => {
    const map = new Map<string, string>();
    questions.forEach(item => map.set(item.questionId, item.prompt));
    return map;
  }, [questions]);

  const issuesBySection = useMemo(() => {
    const map = new Map<string, IArnoValidationIssue[]>();
    issues.forEach(issue => {
      if (issue.sectionId) {
        map.set(issue.sectionId, [...(map.get(issue.sectionId) ?? []), issue]);
      }
    });
    return map;
  }, [issues]);

  const selfGating = useMemo(() => {
    const map = new Map<string, string>();
    selfGatingIssues(sections).forEach(issue =>
      map.set(
        targetKey(issue.target.sectionIndex, issue.target.path),
        prompts.get(issue.questionId) ?? issue.questionId
      )
    );
    return map;
  }, [sections, prompts]);

  const danglingConditions = useMemo(() => {
    const known = new Set([
      ...Array.from(locateQuestions(sections).keys()),
      ...externalQuestions.map(item => item.questionId),
    ]);
    const result = new Set<string>();
    const check = (key: string, condition?: ICondition | null) => {
      if (
        condition?.left.type === 'question' &&
        !known.has(condition.left.questionId ?? '')
      ) {
        result.add(key);
      }
    };
    graph.sections.forEach(layout => {
      check(targetKey(layout.index, null), layout.section.condition);
      layout.rows.forEach(row =>
        check(targetKey(layout.index, row.path), row.node.condition)
      );
    });
    return result;
  }, [sections, externalQuestions, graph]);

  const selectedQuestion =
    selection?.kind === 'node'
      ? nodeAtPath(
          sections[selection.sectionIndex]?.nodes ?? [],
          selection.path
        )
      : null;
  const focusQuestion =
    focusQuestionState ??
    (selectedQuestion?.type === 'question'
      ? (selectedQuestion.questionId ?? null)
      : null) ??
    (selection?.kind === 'external' ? selection.questionId : null);

  const focusTargets = useMemo(() => {
    const set = new Set<string>();
    if (focusQuestion) {
      graph.edges
        .filter(edge => edge.questionId === focusQuestion)
        .forEach(edge =>
          set.add(targetKey(edge.targetRef.sectionIndex, edge.targetRef.path))
        );
    }
    return set;
  }, [graph, focusQuestion]);

  const checkTargetCount = useMemo(() => {
    const map = new Map<string, number>();
    graph.edges.forEach(edge => {
      if (edge.kind === 'field') {
        map.set(edge.source, (map.get(edge.source) ?? 0) + 1);
      }
    });
    return map;
  }, [graph]);

  const overview = useMemo(
    () => questionOverview(sections, externalQuestions),
    [sections, externalQuestions]
  );

  // -------------------------------------------------------------------------
  // Auswahl und Abgleich mit der Liste
  // -------------------------------------------------------------------------
  const selectedSectionIndex =
    selection && 'sectionIndex' in selection ? selection.sectionIndex : null;

  // Nur eine geänderte Auswahl treibt den Abgleich zur Liste; der umgekehrte Weg steht im
  // nächsten Effekt. Ohne die gemerkte Auswahl würden sich beide gegenseitig zurücksetzen.
  const lastSelected = useRef<number | null>(null);
  useEffect(() => {
    if (selectedSectionIndex === lastSelected.current) {
      return;
    }
    lastSelected.current = selectedSectionIndex;
    if (
      selectedSectionIndex !== null &&
      selectedSectionIndex !== focusSectionIndex
    ) {
      onFocusSection(selectedSectionIndex);
    }
  }, [selectedSectionIndex, focusSectionIndex, onFocusSection]);

  const centerOn = useCallback(
    (sectionIndex: number) => {
      const layout = graphRef.current.sections[sectionIndex];
      if (!layout) {
        return;
      }
      flow.setCenter(
        layout.x + layout.width / 2 + 120,
        layout.y + Math.min(layout.height / 2, 220),
        { zoom: flow.getZoom(), duration: 350 }
      );
    },
    [flow]
  );

  const previousFocus = useRef(focusSectionIndex);
  useEffect(() => {
    if (previousFocus.current === focusSectionIndex) {
      return;
    }
    previousFocus.current = focusSectionIndex;
    if (
      selectedSectionIndex !== focusSectionIndex &&
      sections[focusSectionIndex]
    ) {
      setSelection({ kind: 'section', sectionIndex: focusSectionIndex });
      centerOn(focusSectionIndex);
    }
  }, [focusSectionIndex, selectedSectionIndex, sections, centerOn]);

  // -------------------------------------------------------------------------
  // Ändern
  // -------------------------------------------------------------------------
  const used = useCallback(
    () => usedQuestionIds(sectionsRef.current, externalQuestions),
    [externalQuestions]
  );

  const selectMoved = (next: IEditorSection[], node: IEditorNode) => {
    const where = findNodePath(next, node);
    if (where) {
      setSelection({ kind: 'node', ...where });
    }
  };

  const onSectionChange = (index: number, values: Partial<IEditorSection>) => {
    const current = sectionsRef.current;
    if (!current[index]) {
      return;
    }
    commit(
      replaceAt(current, index, { ...current[index], ...values }),
      `section:${index}:${Object.keys(values).join(',')}`
    );
  };

  const onNodeChange = (
    sectionIndex: number,
    path: NodePath,
    node: IEditorNode
  ) => {
    let current = sectionsRef.current;
    const before = nodeAtPath(current[sectionIndex]?.nodes ?? [], path);
    // Eine umbenannte Frage nimmt ihre Linien mit.
    if (
      before?.type === 'question' &&
      node.type === 'question' &&
      before.questionId &&
      node.questionId &&
      before.questionId !== node.questionId
    ) {
      current = renameQuestionReferences(
        current,
        before.questionId,
        node.questionId
      );
    }
    commit(
      replaceNode(current, sectionIndex, path, node),
      `node:${sectionIndex}:${rowKey(path)}`
    );
  };

  const onConditionChange = (
    target: ITargetRef,
    condition: ICondition | null
  ) => {
    commit(
      setConditionAt(sectionsRef.current, target, condition),
      `condition:${targetKey(target.sectionIndex, target.path)}`
    );
    if (!condition) {
      setSelection(
        target.path === null
          ? { kind: 'section', sectionIndex: target.sectionIndex }
          : {
              kind: 'node',
              sectionIndex: target.sectionIndex,
              path: target.path,
            }
      );
    }
  };

  const onCheckChange = (id: string, condition: ICondition) => {
    const pending = pendingChecks.find(item => item.id === id);
    if (pending) {
      setPendingChecks(list =>
        list.map(item => (item.id === id ? { ...item, condition } : item))
      );
      return;
    }
    const check = graphRef.current.checks.find(item => item.id === id);
    if (!check) {
      return;
    }
    commit(
      replaceCheckEverywhere(sectionsRef.current, check.condition, condition),
      `check:${id}`
    );
    // Die Kennung einer Prüfung ist ihr Inhalt — sie wandert mit.
    setSelection({ kind: 'check', id: checkNodeId(condition) });
  };

  const onMoveSection = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= sectionsRef.current.length) {
      return;
    }
    commit(moveSectionTo(sectionsRef.current, index, target));
    setSelection({ kind: 'section', sectionIndex: target });
  };

  const onMoveNode = (sectionIndex: number, path: NodePath, delta: number) => {
    const current = sectionsRef.current;
    const node = nodeAtPath(current[sectionIndex]?.nodes ?? [], path);
    if (!node) {
      return;
    }
    const position = path[path.length - 1];
    const next = moveNode(
      current,
      { sectionIndex, path },
      {
        sectionIndex,
        parentPath: path.slice(0, -1),
        index: delta < 0 ? position - 1 : position + 2,
      }
    );
    commit(next);
    selectMoved(next, node);
  };

  const onMoveNodeToSection = (
    sectionIndex: number,
    path: NodePath,
    target: number
  ) => {
    const current = sectionsRef.current;
    const node = nodeAtPath(current[sectionIndex]?.nodes ?? [], path);
    if (!node || target === sectionIndex || !current[target]) {
      return;
    }
    const next = moveNode(
      current,
      { sectionIndex, path },
      {
        sectionIndex: target,
        parentPath: [],
        index: current[target].nodes.length,
      }
    );
    commit(next);
    selectMoved(next, node);
    centerOn(target);
  };

  const onDuplicateNode = (sectionIndex: number, path: NodePath) => {
    const current = sectionsRef.current;
    const node = nodeAtPath(current[sectionIndex]?.nodes ?? [], path);
    if (!node) {
      return;
    }
    const copy = duplicateNode(node, used());
    const next = insertNode(
      current,
      {
        sectionIndex,
        parentPath: path.slice(0, -1),
        index: path[path.length - 1] + 1,
      },
      copy
    );
    commit(next);
    selectMoved(next, copy);
  };

  const questionIdsIn = (nodes: IEditorNode[]): string[] =>
    walkNodes(nodes)
      .filter(node => node.type === 'question' && node.questionId)
      .map(node => node.questionId as string);

  const deleteSelection = async () => {
    if (!editable || !selection) {
      return;
    }
    const current = sectionsRef.current;
    if (selection.kind === 'edge') {
      commit(
        setConditionAt(
          current,
          { sectionIndex: selection.sectionIndex, path: selection.path },
          null
        )
      );
      setSelection(null);
      return;
    }
    if (selection.kind === 'node') {
      const node = nodeAtPath(
        current[selection.sectionIndex]?.nodes ?? [],
        selection.path
      );
      if (!node) {
        return;
      }
      const ids = questionIdsIn([node]);
      const affected = conditionsOnQuestions(current, ids);
      if (affected > 0) {
        const confirmed = await showConfirmation({
          title: 'Frage löschen?',
          message: `Von dieser Frage hängen ${affected} Bedingung${affected === 1 ? '' : 'en'} ab. Sie werden mit entfernt — die betroffenen Teile erscheinen dann immer.`,
          confirmButtonText: 'Löschen',
          abortButtonText: 'Abbrechen',
        });
        if (!confirmed) {
          return;
        }
      }
      let next = removeNode(current, selection.sectionIndex, selection.path);
      ids.forEach(id => {
        next = dropConditionsOn(next, id);
      });
      commit(next);
      setSelection(null);
      return;
    }
    if (selection.kind === 'section') {
      const section = current[selection.sectionIndex];
      if (!section) {
        return;
      }
      const ids = questionIdsIn(section.nodes);
      const affected = conditionsOnQuestions(current, ids);
      const confirmed = await showConfirmation({
        title: 'Abschnitt löschen?',
        message: `„${section.title}" wird mit allen Elementen entfernt.${affected ? ` ${affected} Bedingung${affected === 1 ? '' : 'en'} auf seine Fragen werden ebenfalls entfernt.` : ''} Mit Strg+Z lässt sich das zurücknehmen, bis gespeichert wird.`,
        confirmButtonText: 'Löschen',
        abortButtonText: 'Abbrechen',
      });
      if (!confirmed) {
        return;
      }
      let next = current.filter((_, index) => index !== selection.sectionIndex);
      ids.forEach(id => {
        next = dropConditionsOn(next, id);
      });
      commit(next);
      setSelection(null);
      return;
    }
    if (selection.kind === 'check') {
      if (pendingChecks.some(item => item.id === selection.id)) {
        setPendingChecks(list => list.filter(item => item.id !== selection.id));
        setSelection(null);
        return;
      }
      const check = graphRef.current.checks.find(
        item => item.id === selection.id
      );
      if (!check) {
        return;
      }
      const count = checkTargetCount.get(check.id) ?? 0;
      if (count > 1) {
        const confirmed = await showConfirmation({
          title: 'Prüfung entfernen?',
          message: `Die Prüfung steuert ${count} Teile. Die Bedingung wird an allen entfernt — sie erscheinen dann immer.`,
          confirmButtonText: 'Entfernen',
          abortButtonText: 'Abbrechen',
        });
        if (!confirmed) {
          return;
        }
      }
      commit(replaceCheckEverywhere(current, check.condition, null));
      setSelection(null);
    }
  };

  // -------------------------------------------------------------------------
  // Hinzufügen per Klick (ohne Ziehen)
  // -------------------------------------------------------------------------
  /** Mitte des sichtbaren Ausschnitts, in Koordinaten der Fläche. */
  const visibleCenterY = (): number => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    const viewport = flow.getViewport();
    return bounds ? (bounds.height / 2 - viewport.y) / viewport.zoom : 0;
  };

  /** Eine neue Prüfung — dort, wo sie abgelegt wurde, sonst mitten im Blick. */
  const addPendingCheck = (nearY: number = visibleCenterY()) => {
    const id = `f:neu:${Date.now()}`;
    setPendingChecks(list => [
      ...list,
      {
        id,
        nearY,
        condition: {
          left: { type: 'selector', selector: '' },
          operator: 'isNotEmpty',
          right: '',
        },
      },
    ]);
    setSelection({ kind: 'check', id });
  };

  const addByClick = (payload: DragPayload) => {
    if (!editable) {
      return;
    }
    const current = sectionsRef.current;
    if ('check' in payload) {
      addPendingCheck();
      return;
    }
    const anchor = selectedSectionIndex ?? current.length - 1;
    if ('section' in payload) {
      const { sections: next, index } = insertSectionAt(current, anchor + 1);
      commit(next);
      setSelection({ kind: 'section', sectionIndex: index });
      setTimeout(() => centerOn(index), 50);
      return;
    }
    if (!('kind' in payload)) {
      return;
    }
    const node = newNode(payload.kind, used());
    if (anchor < 0) {
      const { sections: next, index } = insertSectionAt(current, 0, [node]);
      commit(next);
      setSelection({ kind: 'node', sectionIndex: index, path: [0] });
      return;
    }
    const index =
      selection?.kind === 'node' && selection.sectionIndex === anchor
        ? selection.path[0] + 1
        : current[anchor].nodes.length;
    const next = insertNode(
      current,
      { sectionIndex: anchor, parentPath: [], index },
      node
    );
    commit(next);
    setSelection({ kind: 'node', sectionIndex: anchor, path: [index] });
  };

  // -------------------------------------------------------------------------
  // Ziehen und Ablegen
  // -------------------------------------------------------------------------
  const toFlow = (event: React.DragEvent): XYPosition | null => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) {
      return null;
    }
    return flow.project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });
  };

  const draggedKind = (payload: DragPayload): NodeKind | null => {
    if ('kind' in payload) {
      return payload.kind;
    }
    if ('move' in payload) {
      return (
        nodeAtPath(
          sectionsRef.current[payload.move.sectionIndex]?.nodes ?? [],
          payload.move.path
        )?.type ?? null
      );
    }
    return null;
  };

  const computeDrop = (
    payload: DragPayload,
    position: XYPosition
  ): DropState => {
    if ('check' in payload) {
      return null;
    }
    const layouts = graphRef.current.sections;
    if (!('section' in payload)) {
      const layout = sectionAt(layouts, position.x, position.y);
      if (layout) {
        const point = insertPointAt(
          layout,
          position.y - layout.y,
          draggedKind(payload)
        );
        return {
          type: 'card',
          indicator: {
            sectionIndex: layout.index,
            point,
            markerY: insertMarkerY(layout, point),
            into: point.into,
          },
        };
      }
    }
    return {
      type: 'gap',
      index: sectionInsertIndexAt(layouts, position.y),
      label:
        'section' in payload
          ? 'Neuer Abschnitt hier'
          : 'Neuer Abschnitt mit diesem Element',
    };
  };

  const onDragOver = (event: React.DragEvent) => {
    if (!dragPayload.current || !editable) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect =
      'move' in dragPayload.current ? 'move' : 'copy';
    const position = toFlow(event);
    if (!position) {
      return;
    }
    const next = computeDrop(dragPayload.current, position);
    const key = JSON.stringify(next);
    if (key !== dropKey.current) {
      dropKey.current = key;
      setDropState(next);
    }
  };

  const clearDrag = () => {
    dragPayload.current = null;
    dropKey.current = '';
    setDropState(null);
  };

  const onDrop = (event: React.DragEvent) => {
    const payload = dragPayload.current;
    if (!payload || !editable) {
      return;
    }
    event.preventDefault();
    const position = toFlow(event);
    clearDrag();
    if (!position) {
      return;
    }
    if ('check' in payload) {
      addPendingCheck(position.y);
      return;
    }
    const current = sectionsRef.current;
    const drop = computeDrop(payload, position);
    if (!drop) {
      return;
    }
    if ('section' in payload) {
      const at = drop.type === 'gap' ? drop.index : current.length;
      const { sections: next, index } = insertSectionAt(current, at);
      commit(next);
      setSelection({ kind: 'section', sectionIndex: index });
      return;
    }
    if ('kind' in payload) {
      const node = newNode(payload.kind, used());
      if (drop.type === 'card') {
        const next = insertNode(current, drop.indicator.point, node);
        if (next === current) {
          showAlert({
            severity: 'info',
            text: 'Eine Frage gehört in einen Abschnitt, nicht in eine Bedingung.',
          });
          return;
        }
        commit(next);
        selectMoved(next, node);
      } else {
        const { sections: next, index } = insertSectionAt(current, drop.index, [
          node,
        ]);
        commit(next);
        setSelection({ kind: 'node', sectionIndex: index, path: [0] });
      }
      return;
    }
    const from = payload.move;
    const node = nodeAtPath(current[from.sectionIndex]?.nodes ?? [], from.path);
    if (!node) {
      return;
    }
    if (drop.type === 'card') {
      const next = moveNode(current, from, drop.indicator.point);
      if (next !== current) {
        commit(next);
        selectMoved(next, node);
      }
      return;
    }
    const without = removeNode(current, from.sectionIndex, from.path);
    const { sections: next, index } = insertSectionAt(without, drop.index, [
      node,
    ]);
    commit(next);
    setSelection({ kind: 'node', sectionIndex: index, path: [0] });
  };

  const startPaletteDrag = (event: React.DragEvent, payload: DragPayload) => {
    if (!editable) {
      event.preventDefault();
      return;
    }
    dragPayload.current = payload;
    event.dataTransfer.setData(DND_TYPE, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'copy';
  };

  const startRowDrag = (
    event: React.DragEvent,
    sectionIndex: number,
    path: NodePath
  ) => {
    event.stopPropagation();
    if (!editable) {
      event.preventDefault();
      return;
    }
    const payload: DragPayload = { move: { sectionIndex, path } };
    dragPayload.current = payload;
    event.dataTransfer.setData(DND_TYPE, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';
  };

  // -------------------------------------------------------------------------
  // Abschnitte durch Ziehen der Karte umordnen
  // -------------------------------------------------------------------------
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    changes.forEach(change => {
      if (change.type !== 'position' || !change.position) {
        return;
      }
      const layout = graphRef.current.sections.find(
        item => item.id === change.id
      );
      if (!layout) {
        return;
      }
      setDragOverride({ id: change.id, position: change.position });
      // Wohin der Abschnitt käme — ohne ihn selbst gezählt. Bleibt er, wo er ist, gibt es
      // keine Markierung; sonst steht sie vor der Karte, vor der er landet (in alter Zählung).
      const to = sectionInsertIndexAt(
        graphRef.current.sections,
        change.position.y + layout.height / 2,
        layout.index
      );
      const next: DropState =
        to === layout.index
          ? null
          : {
              type: 'gap',
              index: to < layout.index ? to : to + 1,
              label: 'Abschnitt hierher verschieben',
            };
      const key = JSON.stringify(next);
      if (key !== dropKey.current) {
        dropKey.current = key;
        setDropState(next);
      }
    });
  }, []);

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setDragOverride(null);
      dropKey.current = '';
      setDropState(null);
      const layout = graphRef.current.sections.find(
        item => item.id === node.id
      );
      if (!layout) {
        return;
      }
      const to = sectionInsertIndexAt(
        graphRef.current.sections,
        node.position.y + layout.height / 2,
        layout.index
      );
      if (to !== layout.index) {
        commit(moveSectionTo(sectionsRef.current, layout.index, to));
      }
      setSelection({ kind: 'section', sectionIndex: to });
    },
    [commit]
  );

  // -------------------------------------------------------------------------
  // Verbinden
  // -------------------------------------------------------------------------
  const applyConnection = (
    source: { nodeId: string; handleId: string },
    target: { nodeId: string; handleId: string }
  ) => {
    const result = connect(
      sectionsRef.current,
      graphRef.current,
      source,
      target
    );
    if (!result.ok) {
      showAlert({ severity: 'warning', text: result.message });
      return;
    }
    const ref = graphRef.current.targets.get(
      `${target.nodeId}|${target.handleId}`
    );
    commit(result.sections);
    if (source.nodeId.startsWith('f:neu:')) {
      setPendingChecks(list => list.filter(item => item.id !== source.nodeId));
    }
    if (ref) {
      setSelection({
        kind: 'edge',
        sectionIndex: ref.sectionIndex,
        path: ref.path,
      });
    }
    if (result.replaced) {
      showAlert({
        severity: 'info',
        text: 'Die bisherige Bedingung dieses Teils wurde durch die neue Linie ersetzt.',
      });
    }
  };

  const onConnect = (connection: Connection) => {
    connected.current = true;
    if (!connection.source || !connection.target) {
      return;
    }
    applyConnection(
      { nodeId: connection.source, handleId: connection.sourceHandle ?? '' },
      { nodeId: connection.target, handleId: connection.targetHandle ?? '' }
    );
  };

  /**
   * Eine Linie, die nicht genau auf einem Punkt endet, sondern irgendwo auf einer Karte oder
   * einer bedingten Zeile: gemeint ist trotzdem dieses Ziel. Das spart das Zielen auf 12 Pixel.
   */
  const onConnectEnd = (event: MouseEvent | TouchEvent) => {
    const start = connectStart.current;
    connectStart.current = null;
    if (
      connected.current ||
      !start?.nodeId ||
      !start.handleId ||
      start.handleType !== 'source'
    ) {
      connected.current = false;
      return;
    }
    const point =
      'changedTouches' in event
        ? event.changedTouches[0]
        : (event as MouseEvent);
    const element = document
      .elementFromPoint(point.clientX, point.clientY)
      ?.closest('[data-baukasten-target]');
    const marker = element?.getAttribute('data-baukasten-target');
    if (!marker) {
      return;
    }
    const [type, sectionIndex, key] = marker.split('|');
    const layout = graphRef.current.sections[Number(sectionIndex)];
    if (!layout) {
      return;
    }
    applyConnection(
      { nodeId: start.nodeId, handleId: start.handleId },
      {
        nodeId: layout.id,
        handleId:
          type === 'section'
            ? SECTION_TARGET
            : rowTargetHandle(key.split('.').map(Number)),
      }
    );
  };

  // -------------------------------------------------------------------------
  // Tastatur
  // -------------------------------------------------------------------------
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (isTyping(event.target)) {
      return;
    }
    const mod = event.ctrlKey || event.metaKey;
    if (mod && event.key.toLowerCase() === 'z' && !event.shiftKey) {
      event.preventDefault();
      undo();
    } else if (
      mod &&
      (event.key.toLowerCase() === 'y' ||
        (event.key.toLowerCase() === 'z' && event.shiftKey))
    ) {
      event.preventDefault();
      redo();
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      void deleteSelection();
    } else if (event.key === 'Escape') {
      setSelection(null);
      setFocusQuestion(null);
    }
  };

  // -------------------------------------------------------------------------
  // Knoten und Kanten für React Flow
  // -------------------------------------------------------------------------
  const nodes: Node[] = useMemo(() => {
    const result: Node[] = [
      {
        id: 'start',
        type: 'terminal',
        position: graph.start,
        data: { label: 'Start' },
        draggable: false,
        selectable: false,
      },
      ...graph.sections.map(layout => ({
        id: layout.id,
        type: 'section',
        position:
          dragOverride?.id === layout.id
            ? dragOverride.position
            : { x: layout.x, y: layout.y },
        data: { layout },
        draggable: editable,
        dragHandle: '.baukasten-griff',
        selectable: false,
        zIndex: dragOverride?.id === layout.id ? 10 : 1,
      })),
      {
        id: 'end',
        type: 'terminal',
        position: graph.end,
        data: { label: 'Ende' },
        draggable: false,
        selectable: false,
      },
      ...graph.checks.map(check => ({
        id: check.id,
        type: 'check',
        position: { x: check.x, y: check.y },
        data: { check },
        draggable: false,
        selectable: false,
      })),
      ...graph.externals.map(external => ({
        id: external.id,
        type: 'external',
        position: { x: external.x, y: external.y },
        data: { external },
        draggable: false,
        selectable: false,
      })),
    ];
    return result;
  }, [graph, dragOverride, editable]);

  const edges: Edge[] = useMemo(() => {
    const chain = ['start', ...graph.sections.map(layout => layout.id), 'end'];
    const sequence: Edge[] = chain.slice(1).map((id, position) => ({
      id: `seq:${chain[position]}-${id}`,
      source: chain[position],
      target: id,
      sourceHandle: 'seq-out',
      targetHandle: 'seq-in',
      type: 'straight',
      selectable: false,
      focusable: false,
      style: { stroke: '#b0bec5', strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#b0bec5',
        width: 14,
        height: 14,
      },
    }));
    const conditions: Edge[] = graph.edges.map(edge => {
      const color = EDGE_COLOR[edge.kind];
      const selected =
        selection?.kind === 'edge' && sameTarget(selection, edge.targetRef);
      const focused =
        focusQuestion !== null && edge.questionId === focusQuestion;
      const dimmed = focusQuestion !== null && !focused && !selected;
      const inColumn = edge.kind !== 'field' && !edge.source.startsWith('x:');
      return {
        id: edge.id,
        source: edge.source,
        sourceHandle: edge.sourceHandle,
        target: edge.target,
        targetHandle: edge.targetHandle,
        type: 'smoothstep' as const,
        pathOptions: inColumn
          ? { offset: LANE_BASE + edge.lane * LANE_STEP, borderRadius: 10 }
          : { borderRadius: 10 },
        // Ja/Nein und Optionen stehen schon am Anschluss; eine Beschriftung an der Linie
        // braucht nur ein Vergleich, den der Anschluss nicht sagt (»ist größer als 100 000«).
        label: edge.kind === 'any' ? edge.label || undefined : undefined,
        labelStyle: { fill: color, fontWeight: 600, fontSize: 11 },
        labelBgStyle: { fill: '#ffffff', fillOpacity: 0.92 },
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 4,
        interactionWidth: 16,
        // Nicht animiert: gestrichelt heißt hier »vorgezogene Frage«.
        zIndex: selected || focused ? 5 : 2,
        style: {
          stroke: color,
          strokeWidth: selected || focused ? 3 : 1.75,
          strokeDasharray: edge.backward ? '6 4' : undefined,
          opacity: dimmed ? 0.18 : 1,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color,
          width: 16,
          height: 16,
        },
      };
    });
    return [...sequence, ...conditions];
  }, [graph, selection, focusQuestion]);

  const context: IBaukastenContext = {
    editable,
    selection,
    focusQuestion,
    focusTargets,
    issuesBySection,
    selfGating,
    danglingConditions,
    questions,
    fieldLabel,
    cardDrop: dropState?.type === 'card' ? dropState.indicator : null,
    gapDrop:
      dropState?.type === 'gap'
        ? { index: dropState.index, label: dropState.label }
        : null,
    sectionCount: graph.sections.length,
    checkTargetCount,
    selectSection: index => {
      setFocusQuestion(null);
      setSelection({ kind: 'section', sectionIndex: index });
    },
    selectNode: (sectionIndex, path) => {
      setFocusQuestion(null);
      setSelection({ kind: 'node', sectionIndex, path });
    },
    selectCheck: id => {
      setFocusQuestion(null);
      setSelection({ kind: 'check', id });
    },
    selectExternal: questionId => {
      setFocusQuestion(null);
      setSelection({ kind: 'external', questionId });
    },
    startRowDrag,
    endDrag: clearDrag,
  };

  const conditionCount = graph.edges.length;
  const past = history.current.past.length;
  const future = history.current.future.length;

  return (
    <BaukastenContext.Provider value={context}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='stretch'
        sx={{ height: 'calc(100vh - 250px)', minHeight: 560 }}
      >
        {/* Leiste: Bausteine und Fragen */}
        <Paper
          variant='outlined'
          sx={{ width: 232, flexShrink: 0, overflowY: 'auto', p: 1.25 }}
        >
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
            Bausteine
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            component='div'
            sx={{ mb: 1 }}
          >
            {editable
              ? 'Auf die Fläche ziehen — oder anklicken, dann landet er hinter der Auswahl.'
              : 'Veröffentlicht — nur lesbar.'}
          </Typography>
          {PALETTE.map(group => (
            <Box key={group.title} sx={{ mb: 1.25 }}>
              <Typography
                variant='overline'
                color='text.secondary'
                sx={{ lineHeight: 1.6, display: 'block' }}
              >
                {group.title}
              </Typography>
              <Stack spacing={0.5}>
                {group.items.map(item => (
                  <Tooltip
                    key={item.id}
                    title={item.hint}
                    placement='right'
                    enterDelay={500}
                  >
                    <Box
                      draggable={editable}
                      onDragStart={event =>
                        startPaletteDrag(event, item.payload)
                      }
                      onDragEnd={clearDrag}
                      onClick={() => addByClick(item.payload)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.6,
                        border: 1,
                        borderColor: 'divider',
                        borderLeft: `3px solid ${item.color}`,
                        borderRadius: 1,
                        cursor: editable ? 'grab' : 'not-allowed',
                        opacity: editable ? 1 : 0.5,
                        bgcolor: 'background.paper',
                        userSelect: 'none',
                        '&:hover': editable
                          ? { bgcolor: 'action.hover', boxShadow: 1 }
                          : {},
                      }}
                    >
                      <Box
                        sx={{
                          color: item.color,
                          display: 'flex',
                          fontSize: 18,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography variant='body2'>{item.label}</Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Typography variant='subtitle2'>Fragen</Typography>
          <Typography variant='caption' color='text.secondary' component='div'>
            In der Reihenfolge der Urkunde. Anklicken hebt ihre Zweige hervor.
          </Typography>
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {overview.map(item => (
              <ListItemButton
                key={item.questionId}
                selected={focusQuestion === item.questionId}
                onClick={() => {
                  if (focusQuestion === item.questionId) {
                    setFocusQuestion(null);
                    return;
                  }
                  setFocusQuestion(item.questionId);
                  const where = graph.questionLocation.get(item.questionId);
                  if (where) {
                    setSelection({ kind: 'node', ...where });
                    centerOn(where.sectionIndex);
                  } else {
                    setSelection({
                      kind: 'external',
                      questionId: item.questionId,
                    });
                  }
                }}
                sx={{ borderRadius: 1, px: 1 }}
              >
                <ListItemText
                  primary={item.prompt}
                  primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                  secondary={
                    <Box
                      component='span'
                      sx={{
                        color: item.usedBy ? 'text.secondary' : 'warning.main',
                      }}
                    >
                      {item.external
                        ? `aus ${item.sectionTitle}`
                        : `Abschnitt ${(item.sectionIndex ?? 0) + 1}`}
                      {' · '}
                      {item.usedBy
                        ? `steuert ${item.usedBy} Teil${item.usedBy === 1 ? '' : 'e'}`
                        : 'steuert nichts'}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
            {!overview.length && (
              <Typography variant='caption' color='text.secondary'>
                Noch keine Frage.
              </Typography>
            )}
          </List>
        </Paper>

        {/* Fläche */}
        <Box
          ref={canvasRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragLeave={event => {
            if (
              !canvasRef.current?.contains(
                event.relatedTarget as globalThis.Node
              )
            ) {
              dropKey.current = '';
              setDropState(null);
            }
          }}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: '#fafbfc',
            outline: 'none',
            position: 'relative',
            '&:focus-visible': { borderColor: 'primary.main' },
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onNodeDragStop={onNodeDragStop}
            onConnect={onConnect}
            onConnectStart={(_event, params) => {
              connected.current = false;
              connectStart.current = params;
            }}
            onConnectEnd={onConnectEnd}
            isValidConnection={connection =>
              canConnect(
                graphRef.current,
                {
                  nodeId: connection.source ?? '',
                  handleId: connection.sourceHandle ?? '',
                },
                {
                  nodeId: connection.target ?? '',
                  handleId: connection.targetHandle ?? '',
                }
              )
            }
            onEdgeClick={(_event, edge) => {
              const found = graphRef.current.edges.find(
                item => item.id === edge.id
              );
              if (found) {
                setFocusQuestion(null);
                setSelection({
                  kind: 'edge',
                  sectionIndex: found.targetRef.sectionIndex,
                  path: found.targetRef.path,
                });
              }
            }}
            onPaneClick={() => {
              setSelection(null);
              setFocusQuestion(null);
            }}
            nodesConnectable={editable}
            nodesDraggable={editable}
            elementsSelectable
            deleteKeyCode={null}
            connectionRadius={28}
            minZoom={0.15}
            maxZoom={1.6}
            onInit={showDocumentStart}
          >
            <Background gap={16} size={1} color='#dfe3e8' />
            <Controls showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              nodeColor={node =>
                node.type === 'section'
                  ? '#cfd8dc'
                  : node.type === 'check'
                    ? EDGE_COLOR.field
                    : node.type === 'external'
                      ? '#4fc3f7'
                      : '#455a64'
              }
              style={{ height: 110, width: 160 }}
            />
            <Panel position='top-left'>
              <Paper
                variant='outlined'
                sx={{
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Tooltip title='Rückgängig (Strg+Z)'>
                  <span>
                    <IconButton size='small' onClick={undo} disabled={!past}>
                      <Undo fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Wiederholen (Strg+Y)'>
                  <span>
                    <IconButton size='small' onClick={redo} disabled={!future}>
                      <Redo fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Alles einpassen'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      flow.fitView({ padding: 0.12, duration: 300 })
                    }
                  >
                    <CenterFocusStrong fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ pr: 0.5 }}
                >
                  {sections.length} Abschnitt{sections.length === 1 ? '' : 'e'}{' '}
                  · {overview.length} Frage{overview.length === 1 ? '' : 'n'} ·{' '}
                  {conditionCount} Bedingung{conditionCount === 1 ? '' : 'en'}
                </Typography>
                {!editable && (
                  <Chip
                    size='small'
                    icon={<Lock />}
                    label='nur lesbar'
                    sx={{ ml: 0.5 }}
                  />
                )}
              </Paper>
            </Panel>
            {!sections.length && (
              <Panel position='top-center' style={{ marginTop: 120 }}>
                <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 420 }}>
                  <Typography variant='subtitle1' gutterBottom>
                    Noch leer
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Ziehen Sie einen Abschnitt aus der Leiste hierher — oder
                    einen Baustein: dann entsteht der Abschnitt gleich mit.
                  </Typography>
                  <Button
                    variant='contained'
                    startIcon={<Add />}
                    disabled={!editable}
                    onClick={() => addByClick({ section: true })}
                    sx={{ mt: 2 }}
                  >
                    Ersten Abschnitt anlegen
                  </Button>
                </Paper>
              </Panel>
            )}
          </ReactFlow>
        </Box>

        {/* Eigenschaften */}
        <Paper
          variant='outlined'
          sx={{ width: 440, flexShrink: 0, overflowY: 'auto', p: 1.75 }}
        >
          <BaukastenInspector
            selection={selection}
            sections={sections}
            graph={graph}
            editable={editable}
            questions={questions}
            subBlocks={subBlocks}
            idLegalTransaction={idLegalTransaction}
            fieldLabel={fieldLabel}
            issues={issues}
            selfGating={selfGating}
            pendingChecks={pendingChecks}
            onSectionChange={onSectionChange}
            onNodeChange={onNodeChange}
            onConditionChange={onConditionChange}
            onCheckChange={onCheckChange}
            onMoveSection={onMoveSection}
            onMoveNode={onMoveNode}
            onMoveNodeToSection={onMoveNodeToSection}
            onDuplicateNode={onDuplicateNode}
            onDelete={() => void deleteSelection()}
            onSelect={next => {
              setFocusQuestion(null);
              setSelection(next);
            }}
            onOpenInList={onOpenInList}
          />
        </Paper>
      </Stack>
    </BaukastenContext.Provider>
  );
};

export const DecisionTreeCanvas: React.FC<Props> = props => (
  <ReactFlowProvider>
    <CanvasInner {...props} />
  </ReactFlowProvider>
);
